import type { ReactNode, } from 'react';



/**
 * Props for the Conditional component
 * @param condition - Boolean condition to determine if children should render
 * @param children - React nodes to conditionally render
 * @param fallback - Optional fallback content to render when condition is false
 * @param value - Optional value to compare against the condition
 * @param regex - Optional regex pattern to test against a value
 * @param version - Optional version number for version-based conditional rendering
 */
export declare type ConditionalProps = {
  condition?: boolean;
	value?: number|bigint|string;
	regex?: RegExp;
	version?: number|null; 
  children: ReactNode;
  fallback?: ReactNode;
};



/**
 * A component that conditionally renders its children based on various conditions
 * @param props - The component props containing condition, children, optional fallback, value, regex, and version
 * @returns JSX element or null based on the conditions
 */
export const Conditional = ({
	condition,
	children,
	fallback = null,
	value,
	regex,
	version,
}: ConditionalProps): ReactNode => {

	if([version, condition, regex].every(v => v === undefined)) {
		throw new Error("Conditional: at least one condition must be provided");
	}

	//pass the regex for the children to be displayed
	if(regex && value !== undefined) {
    return regex.test(String(value)) ? children : fallback;
  }

	//pass the version check for the children to be displayed
	if(version !== undefined && value !== undefined) {
		if(typeof value !== "number" && value !== null) {
			throw new Error("Conditional: value must be a number or null when a version is provided");
		}
    const currentVersion = version === null ? 1 : version;; 
    return currentVersion <= value ? children : fallback;
  }

	// pass the condition
	return condition ? children : fallback;
};
